/**
 * @busmgr/report-chart
 *
 * Maps a Custom Report Engine envelope `{ columns, rows, summary, notices }`
 * plus an optional spec `chart` hint into a recharts-ready model.
 *
 * The composer ignores `chart` — it is renderer metadata. Apps that depend on
 * this package still add `recharts` themselves (F-CHARTING / F-VP-CHART).
 */

'use strict';

const CHART_TYPES = Object.freeze(['line', 'bar', 'area']);
const MAX_SERIES = 48;
const CONTROL_KEYS = new Set(['_level', '_group_level']);
const NUMERIC_TYPES = new Set(['currency', 'integer', 'number']);

function columnLabel(envelope, key) {
  const col = (envelope.columns || []).find((c) => c.key === key);
  return (col && col.label) || key;
}

function normalizeHint(raw) {
  if (raw === false || raw === null) return { disabled: true };
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null;
  const type = String(raw.type || '').toLowerCase();
  if (!CHART_TYPES.includes(type)) return null;
  const category = typeof raw.category === 'string' ? raw.category.trim() : '';
  const measure = typeof raw.measure === 'string' ? raw.measure.trim() : '';
  if (!category || !measure) return null;
  const series = typeof raw.series === 'string' && raw.series.trim()
    ? raw.series.trim()
    : null;
  return { type, category, measure, series, disabled: false };
}

/**
 * Spec chart wins when present (including explicit null/false = table-only).
 * Otherwise fall back to the report-type default so already-published L1 specs
 * chart without a re-publish.
 */
function resolveChartHint(specChart, typeDefaultChart) {
  if (specChart !== undefined) {
    const fromSpec = normalizeHint(specChart);
    if (!fromSpec || fromSpec.disabled) return null;
    return fromSpec;
  }
  const fromType = normalizeHint(typeDefaultChart);
  if (!fromType || fromType.disabled) return null;
  return fromType;
}

function skipReason(hint, envelope) {
  if (!hint) return 'no_hint';
  const columns = Array.isArray(envelope && envelope.columns) ? envelope.columns : [];
  const rows = Array.isArray(envelope && envelope.rows) ? envelope.rows : [];
  if (columns.some((c) => CONTROL_KEYS.has(c.key))) return 'control_columns';
  if (rows.length === 0) return 'empty';
  const colMap = new Map(columns.map((c) => [c.key, c]));
  if (!colMap.has(hint.category) || !colMap.has(hint.measure)) return 'missing_columns';
  const measureCol = colMap.get(hint.measure);
  if (!NUMERIC_TYPES.has(measureCol.type)) return 'non_numeric';
  if (hint.series) {
    if (!colMap.has(hint.series)) return 'missing_series';
    const n = new Set(rows.map((r) => String(r[hint.series] == null ? '' : r[hint.series]))).size;
    if (n > MAX_SERIES) return 'too_many_series';
  }
  return null;
}

function canChart(hint, envelope) {
  return skipReason(hint, envelope) === null;
}

function buildChartModel(hint, envelope) {
  if (!canChart(hint, envelope)) return null;
  const rows = envelope.rows;
  const measureCol = (envelope.columns || []).find((c) => c.key === hint.measure);
  const fallbackSeries = columnLabel(envelope, hint.measure);
  const seriesKeys = hint.series
    ? Array.from(new Set(rows.map((r) => String(r[hint.series] == null ? '(blank)' : r[hint.series]))))
    : [fallbackSeries];

  const categories = Array.from(new Set(rows.map((r) => String(r[hint.category] == null ? '' : r[hint.category]))))
    .sort();

  const lookup = new Map();
  for (const r of rows) {
    const cat = String(r[hint.category] == null ? '' : r[hint.category]);
    const ser = hint.series
      ? String(r[hint.series] == null ? '(blank)' : r[hint.series])
      : fallbackSeries;
    const n = parseFloat(r[hint.measure]);
    const key = cat + '\0' + ser;
    lookup.set(key, (lookup.get(key) || 0) + (Number.isFinite(n) ? n : 0));
  }

  const data = categories.map((cat) => {
    const point = { [hint.category]: cat };
    for (const ser of seriesKeys) {
      const v = lookup.get(cat + '\0' + ser);
      point[ser] = v == null ? 0 : v;
    }
    return point;
  });

  return {
    type: hint.type,
    categoryKey: hint.category,
    categoryLabel: columnLabel(envelope, hint.category),
    measureKey: hint.measure,
    measureLabel: (measureCol && measureCol.label) || hint.measure,
    measureType: (measureCol && measureCol.type) || 'number',
    seriesKeys,
    data,
  };
}

module.exports = {
  CHART_TYPES,
  MAX_SERIES,
  resolveChartHint,
  skipReason,
  canChart,
  buildChartModel,
};
